import { Component, type ErrorInfo, type ReactNode } from "react";

const RELOAD_KEY = "chunk-reload-at";
const COOLDOWN_MS = 30000;

/**
 * Routes are code-split, so a stale hashed chunk from a previous deploy can fail
 * to load after the site is republished and leave #root empty. This boundary
 * catches that specific error and reloads once (with a cooldown so a genuinely
 * missing chunk cannot loop), otherwise shows a small recoverable fallback.
 */
export default class ChunkErrorBoundary extends Component<
  { children: ReactNode },
  { hasError: boolean }
> {
  state = { hasError: false };

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: Error, _info: ErrorInfo) {
    const msg = String(error?.message || "");
    const isChunkError =
      /Loading chunk|dynamically imported module|Importing a module script failed|ChunkLoadError|Failed to fetch/i.test(
        msg,
      );
    if (isChunkError) {
      const last = Number(sessionStorage.getItem(RELOAD_KEY) || 0);
      if (Date.now() - last > COOLDOWN_MS) {
        sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
        window.location.reload();
      }
    }
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex min-h-[60vh] flex-col items-center justify-center gap-4 px-4 text-center">
          <p className="text-muted-foreground">Something went wrong loading this page.</p>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="rounded-lg bg-primary px-5 py-2.5 font-semibold text-primary-foreground"
          >
            Reload
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}
